// Canonical heading. Plan 24 centralises the repeated
// `"text-3xl/4xl/5xl font-serif tracking-tight"` blobs behind a single
// primitive so typography changes propagate in one place.
//
// `level` controls BOTH the rendered element (`<h1>`-`<h4>`) and the
// default size. Callers that need a size/weight tweak pass `className`
// (merged via `cn()` last-writer-wins).

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

export type HeadingLevel = 1 | 2 | 3 | 4;

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level: HeadingLevel;
  children: ReactNode;
}

const BASE = 'font-serif tracking-tight text-stone-900';

const SIZE_BY_LEVEL: Record<HeadingLevel, string> = {
  1: 'text-5xl md:text-6xl',
  2: 'text-4xl',
  3: 'text-3xl',
  4: 'text-2xl',
};

export const Heading = ({ level, className, children, ...rest }: HeadingProps) => {
  const Tag = `h${level}` as const;
  return (
    <Tag className={cn(BASE, SIZE_BY_LEVEL[level], className)} {...rest}>
      {children}
    </Tag>
  );
};
